import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShakedownInput, ShakedownResults, ErrorState, LoadingState } from '../components';
import {
  analyzeShakedown,
  saveShakedown,
  type AnalyzeShakedownRequest,
  type ShakedownResult,
  type ShakedownInputType,
} from '../lib/api';

export function Shakedown() {
  const [result, setResult] = useState<ShakedownResult | null>(null);
  const [inputData, setInputData] = useState<{
    type: ShakedownInputType;
    content: string;
    context?: AnalyzeShakedownRequest['context'];
  } | null>(null);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);

  const analyzeMutation = useMutation({
    mutationFn: analyzeShakedown,
    onSuccess: (data) => {
      setResult(data);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!inputData || !result) {
        throw new Error('No data to save');
      }
      return saveShakedown(inputData.type, inputData.content, result, inputData.context);
    },
    onSuccess: (data) => {
      setSavedUrl(data.url);
    },
  });

  const handleSubmit = (request: AnalyzeShakedownRequest) => {
    setInputData({
      type: request.type,
      content: request.content,
      context: request.context,
    });
    setResult(null);
    setSavedUrl(null);
    analyzeMutation.mutate(request);
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const handleReset = () => {
    setResult(null);
    setInputData(null);
    setSavedUrl(null);
    analyzeMutation.reset();
    saveMutation.reset();
  };

  const handleRetry = () => {
    if (inputData) {
      analyzeMutation.mutate({
        type: inputData.type,
        content: inputData.content,
        context: inputData.context,
      });
    } else {
      handleReset();
    }
  };

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      {/* Header - show when no result or analyzing */}
      {!result && !analyzeMutation.isPending && !analyzeMutation.isError && (
        <div className="max-w-4xl mx-auto text-center mb-12 animate-fadeIn">
          <h1 className="font-mono text-4xl sm:text-5xl font-bold mb-6 leading-tight">
            Shakedown Your Gear
          </h1>
          <p className="text-lg sm:text-xl text-neutral-400 max-w-xl mx-auto mb-6">
            Paste your gear list. We'll match it to our database, find weight savings,
            and identify what you're missing.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm text-neutral-500">
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Product matching
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Weight optimization
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Missing essentials
            </span>
          </div>
        </div>
      )}

      {/* Input Form - show when no result and not loading */}
      {!result && !analyzeMutation.isPending && !analyzeMutation.isError && (
        <ShakedownInput onSubmit={handleSubmit} isLoading={analyzeMutation.isPending} />
      )}

      {/* Loading State */}
      {analyzeMutation.isPending && (
        <div className="max-w-2xl mx-auto">
          <LoadingState />
          <p className="text-center text-neutral-500 mt-4">
            Analyzing your gear list and finding optimizations...
          </p>
        </div>
      )}

      {/* Error State */}
      {analyzeMutation.isError && (
        <ErrorState
          message={analyzeMutation.error?.message || 'Failed to analyze gear list'}
          onRetry={handleRetry}
        />
      )}

      {/* Results */}
      {result && !analyzeMutation.isPending && (
        <ShakedownResults
          result={result}
          onSave={handleSave}
          onReset={handleReset}
          isSaving={saveMutation.isPending}
          savedUrl={savedUrl || undefined}
        />
      )}

      {/* CTA Section */}
      {!result && !analyzeMutation.isPending && !analyzeMutation.isError && (
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="text-neutral-500 mb-4">
            Don't have a gear list yet?
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 font-medium"
          >
            <span>Build a kit from scratch</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      )}
    </main>
  );
}
